/**
 * Tỉ trọng thực của nhóm, suy từ `ty_trong` theo tiêu chí mà backend trả về (đặc tả mục 5.4).
 *
 * Backend tính theo **tiêu chí** (`cham-diem/cham.ts` → `tyTrong`), nhưng giao diện v3 hiện % cho cả nhóm.
 * Cộng lại ở đây thay vì tự tính lại từ trọng số: phép tính thật nằm ở backend và nó loại nhóm không hợp lệ
 * (không có tiêu chí, hoặc tổng trọng số tiêu chí bằng 0) — tính lại ở client là mở đường cho hai con số
 * khác nhau trên cùng một màn hình.
 */
import type { BanNhap, NhomTieuChi } from '../types';

/** Tổng tỉ trọng của một nhóm theo %, làm tròn tới 0,1. */
export function tyTrongNhom(nhom: NhomTieuChi, tyTrong: Record<string, number> | undefined): number {
  if (!tyTrong) return 0;
  const tong = nhom.tieu_chi.reduce((s, t) => s + (tyTrong[t.ma] ?? 0), 0);
  return Math.round(tong * 10) / 10;
}

/** Tỉ trọng một tiêu chí theo %, làm tròn tới 0,1. */
export function tyTrongTieuChi(ma: string, tyTrong: Record<string, number> | undefined): number {
  return Math.round((tyTrong?.[ma] ?? 0) * 10) / 10;
}

/** Dài nhất của một mã trước hậu tố chống trùng; cột `ma` của backend là varchar(40). */
const DAI_TOI_DA = 34;

/**
 * Chuẩn hoá tên thành mã. **Chép quy tắc từ `modules/tham-dinh/backend/src/cham-diem/sinh-ma.ts`** — tệp đó
 * nói thẳng là frontend phải dùng cùng quy tắc, nhưng module không chia sẻ mã nguồn với web (ADR-0051), nên
 * hai bản sao giữ khớp nhau bằng test: xem `ty-trong.test.ts`.
 *
 * `đ`/`Đ` xử riêng vì NFD **không** phân rã được chữ này: quên nó thì `đầu tư` ra `_u_tu`, không phải `dau_tu`.
 */
export function chuanHoaMa(ten: string): string {
  return ten
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, DAI_TOI_DA)
    // Cắt 34 ký tự có thể để lại gạch dưới ở cuối; bỏ nó để mã không có đuôi lạ.
    .replace(/_+$/, '');
}

/**
 * Sinh mã từ tên, chống trùng bằng hậu tố `_2`, `_3`…
 *
 * Mã là khoá **ổn định**: `ket_qua_tieu_chi.ma` trỏ vào nó, nên đổi mã là mất nối với mọi kết quả đã chấm.
 * Vì vậy mã chỉ sinh **một lần lúc thêm tiêu chí**; sửa tên về sau không sinh lại.
 *
 * `daDung` gộp cả mã nhóm lẫn mã tiêu chí, đúng luật kiểm ở đặc tả mục 5.3 điều 2.
 */
export function sinhMa(ten: string, daDung: ReadonlySet<string> = new Set()): string {
  const goc = chuanHoaMa(ten) || 'tieu_chi';
  if (!daDung.has(goc)) return goc;
  for (let i = 2; ; i += 1) {
    const hauTo = `_${i}`;
    // Cắt gốc để mã kèm hậu tố vẫn trong trần, nếu không mã thứ hai dài hơn mã thứ nhất.
    const ma = `${goc.slice(0, DAI_TOI_DA - hauTo.length).replace(/_+$/, '')}${hauTo}`;
    if (!daDung.has(ma)) return ma;
  }
}

/** Mọi mã đang dùng trong một bản nháp, để `sinhMa` tránh trùng. */
export function maDangDung(ban: BanNhap): Set<string> {
  const ra = new Set<string>();
  for (const n of ban.nhom) {
    ra.add(n.ma);
    for (const t of n.tieu_chi) ra.add(t.ma);
  }
  return ra;
}
